import { Component, computed, input, signal } from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';
import { DocxViewerComponent } from '../docx-viewer/docx-viewer.component';
import { ImgViewerComponent } from '../image-viewer/img-viewer.component';

type ViewerType = 'pdf' | 'docx' | 'image';
type PreviewState = ViewerType | 'missing-url' | 'unsupported';

const FILE_TYPE_VIEWER_MAP: Readonly<Record<string, ViewerType>> = {
  pdf: 'pdf',
  docx: 'docx',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  svg: 'image'
};

@Component({
  selector: 'lib-file-previewer',
  standalone: true,
  imports: [ButtonModule, PdfViewerComponent, DocxViewerComponent, ImgViewerComponent],
  templateUrl: './file-previewer.component.html',
  styleUrl: './file-previewer.component.scss'
})
export class FilePreviewerComponent {
  url = input.required<string>();
  fileType = input.required<string>();
  height = input<string>('');

  protected readonly isDownloading = signal(false);

  protected readonly hasUrl = computed(() => this.url().trim().length > 0);

  protected readonly normalizedFileType = computed(() => this.fileType().trim().toLowerCase().replace(/^\./, ''));

  protected readonly state = computed<PreviewState>(() => {
    if (!this.hasUrl()) {
      return 'missing-url';
    }
    return FILE_TYPE_VIEWER_MAP[this.normalizedFileType()] ?? 'unsupported';
  });

  async download(): Promise<void> {
    const fileUrl = this.url();
    if (!fileUrl || this.isDownloading()) {
      return;
    }

    this.isDownloading.set(true);
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      this.#triggerAnchorDownload(objectUrl);
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.#triggerAnchorDownload(fileUrl);
    } finally {
      this.isDownloading.set(false);
    }
  }

  #triggerAnchorDownload(href: string): void {
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = this.#suggestedFileName();
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  #suggestedFileName(): string {
    const nameFromUrl = this.url().split('/').pop()?.split('?')[0]?.split('#')[0];
    if (nameFromUrl?.includes('.')) {
      return nameFromUrl;
    }
    const extension = this.normalizedFileType();
    return extension ? `download.${extension}` : 'download';
  }
}
